import { Icons } from './Icons';
import type { UploadedAudio } from '@/types';

interface AudioLibraryProps {
  isDragging: boolean;
  uploadingIds: string[];
  audios: UploadedAudio[];
  playingAudio: string | null;
  onDragOver: (e: React.DragEvent) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent) => void;
  onPlay: (audioId: string, url?: string) => void;
  onInsert: (audioId: string, label: string) => void;
}

export function AudioLibrary({
  isDragging,
  uploadingIds,
  audios,
  playingAudio,
  onDragOver,
  onDragLeave,
  onDrop,
  onPlay,
  onInsert
}: AudioLibraryProps) {
  return (
    <div className="space-y-4">
      {/* Audio Upload Zone */}
      <div
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        className={`rounded-lg border-2 border-dashed p-6 text-center transition-all ${
          isDragging 
            ? 'border-teal-400 bg-teal-50' 
            : 'border-stone-300 hover:border-stone-400'
        }`}
      >
        <Icons.Upload />
        <p className="text-sm text-stone-600 mt-2">Drop audio files here</p>
        <p className="text-xs text-stone-400 mt-1">WAV, MP3, M4A</p>
      </div>

      {/* Audio List */}
      {(audios.length > 0 || uploadingIds.length > 0) && (
        <div className="bg-white rounded-lg border border-stone-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-stone-100 bg-stone-50">
            <p className="text-xs font-medium text-stone-500 uppercase tracking-wide">Uploaded Audio</p>
          </div>
          <div className="divide-y divide-stone-100 max-h-80 overflow-y-auto">
            {uploadingIds.map((id) => (
              <div key={id} className="px-4 py-3 flex items-center gap-3 text-stone-400">
                <div className="w-4 h-4 border-2 border-stone-200 border-t-teal-500 rounded-full animate-spin" />
                <span className="text-sm">Uploading {id}...</span>
              </div>
            ))}
            {audios.map((audio) => (
              <div key={audio.audioId} className="px-4 py-3 flex items-center justify-between group hover:bg-stone-50 transition-colors">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-stone-700 truncate">{audio.label}</p>
                  <p className="text-xs text-stone-400 truncate">{audio.audioId}</p>
                </div>
                <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => onPlay(audio.audioId, audio.url)}
                    className="p-2 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                  >
                    {playingAudio === audio.audioId ? <Icons.Pause /> : <Icons.Play />}
                  </button>
                  <button
                    onClick={() => onInsert(audio.audioId, audio.label)}
                    className="p-2 text-stone-400 hover:text-teal-600 hover:bg-teal-50 rounded transition-colors"
                    title="Insert"
                  >
                    <Icons.Plus />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
